import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Appointment, Bundle } from 'fhir/r4';
import { UtilityService } from '../shared/utility.service';
import { AuthService } from '../shared/auth/auth.service';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, Subscription, tap, forkJoin, of, catchError, Observable, firstValueFrom } from 'rxjs';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { isHospitalAdmin, isHealthcareAdmin } from '../shared/system-roles';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppointmentService } from '../services/appointment.service';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AppointmentDetailsComponent } from './appointment-details/appointment-details.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TableFilteringService, TableRowModel } from '../shared/table-filtering.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { AdmittedPatientsComponent } from '../admitted-patients/admitted-patients.component';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { ChipsDirective } from '../chips.directive';
import { BookingsFormComponent } from '../bookings-form/bookings-form.component';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { ShiftCascadeDialogComponent } from './shift-cascade-dialog/shift-cascade-dialog.component';
import { UnifiedNotificationService } from '../shared/unified-notification.service';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { LoadingUIEnabled, SkipHospitalSegregation } from '../loading.interceptor';

@Component({
  selector: 'app-appointment',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatChipsModule,
    MatInputModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatDialogModule,
    MatFormFieldModule,
    ChipsDirective,
    DatePipe,
    MatTooltipModule,
    EmptyStateComponent,
    TableHeaderComponent,
    PatientNamePipe,
    PatientContactsPipe,
    PatientAgePipe,
    PatientAddressPipe,
    ReferenceDisplayDirective,
    
    EmphasisDirective,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    RouterModule

  ],
  providers: [PatientNamePipe, PatientContactsPipe, PatientAgePipe, PatientAddressPipe],
  templateUrl: './appointment.component.html',
  styleUrls: ['./appointment.component.scss']
})

export class AppointmentComponent extends AdmittedPatientsComponent {
  utilityService = inject(UtilityService);
  authService = inject(AuthService);
  override router = inject(Router);
  private route = inject(ActivatedRoute);
  appointmentService = inject(AppointmentService);
  sn = inject(MatSnackBar);
  override dialog = inject(MatDialog);
  private tfs = inject(TableFilteringService<Appointment, any>);
  private fb = inject(FormBuilder);
  private readonly fhirHttp = inject(HttpClient);
  private readonly fhirBackendUrl = inject(backendEndPointToken);
  private readonly terminologyUrl = inject(terminologyCanonicalUrlBaseToken);
  private unifiedNotificationService = inject(UnifiedNotificationService);
  private fhirResourceService = inject(FhirResourceService);

  tableFilterArray: Map<string, string[]> = new Map([
    ['status', ['proposed', 'pending', 'booked', 'arrived', 'no-show', 'fulfilled', 'cancelled', 'waitlist']],
    ['serviceCategory', ['Telemedicine', 'Consultation']],
    ['dateRange', ['Today', 'This Week', 'This Month', 'Last 30 Days', 'This Year', 'All Time']]
  ]);

  tableFiltersFormControlObject: { [key: string]: FormGroup } = {};

  // Track whether the initial fetch was already scoped by date range, and which statuses were loaded.
  private initialFetchWasDateFiltered = false;
  private currentLoadedStatusSet = new Set<string>();
  private allLoadedAppointments: any[] = [];
  private previousDateRangeFilters: string[] = [];
  private hasAppliedInitialQueryParams = false;
  private readonly activeStatusPreset = ['proposed', 'pending', 'booked', 'arrived', 'waitlist'];
  private readonly completedStatusPreset = ['fulfilled', 'cancelled'];
  private readonly allStatusPreset = ['proposed', 'pending', 'booked', 'arrived', 'fulfilled', 'cancelled', 'waitlist'];
  selectedStatusTab: 'active' | 'completed' | 'all' | 'custom' = 'active';
  // Expose paging observables for template
  displayedTotal$ = this.tfs.displayedTotal$;
  tfsLoading$ = this.tfs.loading$;
  pageSize = 25;

  // Storage for the merged raw data before TFS local filtering
  private rawAppointmentsSubject = new BehaviorSubject<Appointment[]>([]);
  loading$ = this.tfs.loading$;

  private readonly meetingUrlRegex = /(https?:\/\/[^\s]+)/i;

  override ngOnInit() {
    this.displayedColumns = this.isAdmin()
      ? ['date', 'status', 'type', 'patient', 'practitioner', 'actions']
      : ['date', 'status', 'type', 'patient', 'actions'];

    // Initialize filter form groups
    for (const [key, values] of this.tableFilterArray) {
      this.tableFiltersFormControlObject[key] = this.fb.group(
        values.reduce((acc: any, val) => {
          acc[val] = [false];
          return acc;
        }, {})
      );
    }

    // Initialize TableFilteringService in local mode
    this.tfs.init({
      resourceType: 'Appointment',
      defaultQuery: '',
      dataSource: this.tableDataSource,
      enableLocalFiltering: true,
      localFilterPredicate: (row, filters) => this.customFilterPredicate(row, filters),
      transformEntry: (resource) => this.toAppointmentTableRow(resource)
    });

    this.subs.add(
      this.route.queryParamMap.subscribe((params) => this.applyQueryParamFilters(params))
    );

    this.fetchData();
  }

  override ngOnDestroy(): void {
    this.tfs.destroy();
    this.tableDataSource.data = [];
    this.tfs.setLocalRows([]);
    this.rawAppointmentsSubject.next([]);
    this.rawAppointmentsSubject.complete();
    this.allLoadedAppointments = [];
    this.currentLoadedStatusSet.clear();
    super.ngOnDestroy();
  }

  private applyQueryParamFilters(params: ParamMap): void {
    if (this.hasAppliedInitialQueryParams) {
      return;
    }

    const hasStatusQuery = params.has('status');

    for (const filterKey of this.tableFilterArray.keys()) {
      const formGroup = this.tableFiltersFormControlObject[filterKey];
      if (!formGroup) continue;

      Object.keys(formGroup.controls).forEach(controlName => {
        const shouldSelect = this.queryParamMatchesFilterOption(params, filterKey, controlName);
        formGroup.get(controlName)?.setValue(shouldSelect, { emitEvent: false });
      });
    }

    if (this.tableFiltersFormControlObject['dateRange']) {
      this.normalizeDateRangeSelection(this.getSelectedFilterValues('dateRange'));
    }

    const tab = (params.get('tab') || '').toLowerCase();
    if (tab === 'completed' || tab === 'all' || tab === 'active') {
      this.selectedStatusTab = tab as 'active' | 'completed' | 'all';
    } else {
      this.selectedStatusTab = 'active';
    }

    if (!hasStatusQuery) {
      this.setStatusTab(this.selectedStatusTab, false);
    } else {
      this.updateSelectedStatusTabFromStatuses(this.getSelectedFilterValues('status'));
    }

    this.hasAppliedInitialQueryParams = true;
    this.tfs.search(this.getCurrentFilters());
  }

  private queryParamMatchesFilterOption(params: ParamMap, filterKey: string, option: string): boolean {
    const values = params.getAll(filterKey)
      .flatMap(value => value.split(','))
      .map(value => this.normalizeQueryFilterValue(filterKey, value))
      .filter(Boolean);

    return values.includes(this.normalizeQueryFilterValue(filterKey, option));
  }

  private normalizeQueryFilterValue(filterKey: string, value: string): string {
    const normalized = (value ?? '').toString().trim().toLowerCase();
    if (!normalized) return '';

    if (filterKey === 'status') {
      if (['no-show', 'no show', 'noshow'].includes(normalized)) return 'noshow';
    }

    if (filterKey === 'serviceCategory') {
      if (['telemedicine', 'telemed', 'remote', 'virtual'].includes(normalized)) return 'telemedicine';
      if (['consultation', 'consult', 'consultations'].includes(normalized)) return 'consultation';
    }

    return normalized.replace(/\s+/g, '-');
  }

  private fetchData() {
    const userRole = this.authService.user.getValue()?.role;
    const isAdministrative = isHospitalAdmin(userRole) || isHealthcareAdmin(userRole);
    const orgId = this.authService.user.getValue()?.organizationId;
    const actorRef = this.getLoggedInActorReference();

    const dateRangeForm = this.tableFiltersFormControlObject['dateRange'];
    const isDateFiltered = dateRangeForm ? Object.values(dateRangeForm.value).some(v => v === true) : false;
    this.initialFetchWasDateFiltered = isDateFiltered;

    const statusLimit = !isDateFiltered ? 'proposed,booked,arrived,pending,waitlist' : '';
    const requestedStatuses = this.getRequestedStatusesFromFilters();
    const missingStatuses = this.getMissingStatusesToFetch(requestedStatuses);

    if (isAdministrative && orgId) {
      const owned$ = this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
        resourceType: 'Appointment',
        urlSearchObject: {
          _tag: orgId,
          ...(statusLimit ? { status: statusLimit } : {})
        },
        scope: 'orgWide',
        tier1FollowNextLinks: true,
        tier1MaxPages: 100
      });

      const shared$ = this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
        resourceType: 'Appointment',
        urlSearchObject: {
          _security: orgId,
          ...(statusLimit ? { status: statusLimit } : {})
        },
        scope: 'orgWide',
        tier1FollowNextLinks: true,
      });

      combineLatest([owned$, shared$]).pipe(
        map(([owned, shared]) => {
          const combined = [...(owned || []), ...(shared || [])];
          const deduped = this.dedupeAppointments(combined);
          return this.applyLoadedAppointments(deduped);
        }),
        tap(data => this.tfs.setLocalRows(data))
      ).subscribe(() => {
        if (!this.initialFetchWasDateFiltered && missingStatuses.length) {
          this.fetchAdditionalStatusesForCurrentFilters(isAdministrative, orgId, actorRef);
        }
      });
    } else {
      this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
        resourceType: 'Appointment',
        urlSearchObject: {
          actor: actorRef,
          ...(statusLimit ? { status: statusLimit } : {})
        },
        context: new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true),
        querySearchObject: [
          { key: 'participant.actor.reference', value: [actorRef, `${this.backendUrl}/${actorRef}`] }
        ],
        scope: 'orgWide',
        tier1FollowNextLinks: true
      }).pipe(
        map(data => {
          const rows = Array.isArray(data) ? data : [];
          return this.applyLoadedAppointments(rows);
        }),
        tap(data => this.tfs.setLocalRows(data || []))
      ).subscribe(() => {
        if (!this.initialFetchWasDateFiltered && missingStatuses.length) {
          this.fetchAdditionalStatusesForCurrentFilters(isAdministrative, orgId, actorRef);
        }
      });
    }
  }

  private resolveAppointmentDisplayType(a: Appointment): string {
    return this.resolveAppointmentServiceCategory(a) ||
      a.serviceType?.[0]?.coding?.[0]?.display ||
      a.serviceType?.[0]?.text ||
      a.appointmentType?.coding?.[0]?.display ||
      a.appointmentType?.text ||
      a.description ||
      'General';
  }

  getAppointmentMode(appointment: Appointment): string {
    const modeCoding = appointment.appointmentType?.coding?.find(coding =>
      coding.system === 'http://e-likita.org/fhir/CodeSystem/appointment-mode' ||
      coding.system === `${this.terminologyUrl}/CodeSystem/appointment-mode`
    ) ?? appointment.appointmentType?.coding?.[0];

    const modeCode = this.normalizeTerminologyValue(modeCoding?.code);
    const modeDisplay = this.normalizeTerminologyValue(modeCoding?.display);
    const modeText = this.normalizeTerminologyValue(appointment.appointmentType?.text);

    if (this.isVirtualAppointmentType(modeCode, modeDisplay, modeText)) {
      return 'Virtual';
    }

    if (this.isInPersonAppointmentType(modeCode, modeDisplay, modeText)) {
      return 'In Person';
    }

    return this.isTelemedicine(appointment) ? 'Virtual' : 'In Person';
  }

  private resolveAppointmentServiceCategory(a: Appointment): string {
    for (const category of a.serviceCategory || []) {
      const categoryText = this.normalizeTerminologyValue(category.text);

      for (const coding of category.coding || []) {
        const code = this.normalizeTerminologyValue(coding.code);
        const display = this.normalizeTerminologyValue(coding.display);

        if (this.isTelemedicineServiceCategory(code, display, categoryText)) {
          return 'Telemedicine';
        }

        if (this.isConsultationServiceCategory(code, display, categoryText)) {
          return 'Consultation';
        }
      }

      if (categoryText) {
        if (this.isTelemedicineServiceCategory('', categoryText, categoryText)) {
          return 'Telemedicine';
        }

        if (this.isConsultationServiceCategory('', categoryText, categoryText)) {
          return 'Consultation';
        }

        return category.text || '';
      }
    }

    return this.resolveLegacyServiceCategoryFromAppointmentType(a);
  }

  private normalizeTerminologyValue(value: string | undefined | null): string {
    return (value ?? '').trim().toLowerCase();
  }

  private isVirtualAppointmentType(code: string, display: string, text: string): boolean {
    return code === 'virtual' ||
      code === 'tele' ||
      display.includes('virtual') ||
      display.includes('telehealth') ||
      display.includes('telemed') ||
      text.includes('virtual') ||
      text.includes('telehealth') ||
      text.includes('telemed');
  }

  private isInPersonAppointmentType(code: string, display: string, text: string): boolean {
    return code === 'in-person' ||
      code === 'in person' ||
      code === 'routine' ||
      display.includes('in-person') ||
      display.includes('in person') ||
      display.includes('routine') ||
      text.includes('in-person') ||
      text.includes('in person') ||
      text.includes('routine');
  }

  private isTelemedicineServiceCategory(code: string, display: string, text: string): boolean {
    return code === 'telemedicine' ||
      code === 'telemed' ||
      code === 'tele' ||
      code === 'virtual' ||
      display.includes('telemedicine') ||
      display.includes('telemed') ||
      display.includes('telehealth') ||
      display.includes('virtual') ||
      text.includes('telemedicine') ||
      text.includes('telemed') ||
      text.includes('telehealth') ||
      text.includes('virtual');
  }

  private isConsultationServiceCategory(code: string, display: string, text: string): boolean {
    return code === 'consultation' ||
      code === 'consult' ||
      code === 'con' ||
      code === 'routine' ||
      code === 'outpatient-consultation' ||
      display.includes('consultation') ||
      display.includes('consult') ||
      display.includes('routine') ||
      text.includes('consultation') ||
      text.includes('consult') ||
      text.includes('routine');
  }

  private resolveLegacyServiceCategoryFromAppointmentType(a: Appointment): string {
    const typeText = this.normalizeTerminologyValue(a.appointmentType?.text);

    for (const coding of a.appointmentType?.coding || []) {
      const code = this.normalizeTerminologyValue(coding.code);
      const display = this.normalizeTerminologyValue(coding.display);

      if (code === 'tele' || display.includes('telemedicine') || display.includes('telemed') || typeText.includes('telemedicine') || typeText.includes('telemed')) {
        return 'Telemedicine';
      }

      if (code === 'con' || code === 'routine' || display.includes('consultation') || display.includes('routine') || typeText.includes('consultation') || typeText.includes('routine')) {
        return 'Consultation';
      }
    }

    return '';
  }

  onFilterChanged(event: { filterKey: string; values: string[] }) {
    console.log('Filter changed:', event.filterKey, event.values);
    const values = event.filterKey === 'dateRange'
      ? this.normalizeDateRangeSelection(event.values)
      : event.values;
    this.clearInitialQueryParamsFromUrl();

    if (event.filterKey === 'status') {
      this.updateSelectedStatusTabFromStatuses(values);
    }

    const allFilters = { ...this.getCurrentFilters(), [event.filterKey]: values };
    this.tfs.search(allFilters);

    if (event.filterKey === 'status') {
      this.ensureMissingStatusesLoadedForCurrentFilters(values);
    }
  }

  private normalizeDateRangeSelection(values: string[]): string[] {
    const selected = values ?? [];
    const previous = this.previousDateRangeFilters;
    const allTime = 'All Time';

    let normalized = selected;
    if (selected.includes(allTime) && selected.length > 1) {
      const allTimeWasJustSelected = !previous.includes(allTime);
      normalized = allTimeWasJustSelected
        ? [allTime]
        : selected.filter(value => value !== allTime);
    }

    const dateRangeGroup = this.tableFiltersFormControlObject['dateRange'];
    if (dateRangeGroup) {
      Object.keys(dateRangeGroup.controls).forEach(controlName => {
        const shouldBeSelected = normalized.includes(controlName);
        if (dateRangeGroup.get(controlName)?.value !== shouldBeSelected) {
          dateRangeGroup.get(controlName)?.setValue(shouldBeSelected, { emitEvent: false });
        }
      });
    }

    this.previousDateRangeFilters = normalized;
    return normalized;
  }

  onSearchChanged(term: string) {
    this.clearInitialQueryParamsFromUrl();
    const allFilters = { ...this.getCurrentFilters(), _search: term };
    this.tfs.search(allFilters);
    this.ensureMissingStatusesLoadedForCurrentFilters();
  }

  private clearInitialQueryParamsFromUrl(): void {
    if (!this.hasAppliedInitialQueryParams) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        status: null,
        serviceCategory: null,
        dateRange: null,
        tab: null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private getCurrentFilters(): any {
    const filters: any = {};
    Object.keys(this.tableFiltersFormControlObject).forEach(key => {
      const selected = this.getSelectedFilterValues(key);
      if (selected.length) filters[key] = selected;
    });
    return filters;
  }

  private getSelectedFilterValues(filterKey: string): string[] {
    const formGroup = this.tableFiltersFormControlObject[filterKey];
    if (!formGroup) return [];

    return Object.entries(formGroup.value)
      .filter(([_, val]) => val === true)
      .map(([key, _]) => key);
  }

  setStatusTab(tab: 'active' | 'completed' | 'all', updateQueryParam = true): void {
    this.clearInitialQueryParamsFromUrl();
    this.selectedStatusTab = tab;
    const statusGroup = this.tableFiltersFormControlObject['status'];
    if (!statusGroup) return;

    const allStatuses = Object.keys(statusGroup.controls);
    allStatuses.forEach(status => statusGroup.get(status)?.setValue(false, { emitEvent: false }));

    const statusesToSelect = tab === 'completed'
      ? this.completedStatusPreset
      : tab === 'all'
        ? this.allStatusPreset
        : this.activeStatusPreset;

    statusesToSelect.forEach(status => {
      if (statusGroup.get(status)) {
        statusGroup.get(status)?.setValue(true, { emitEvent: false });
      }
    });

    if (updateQueryParam && !this.hasAppliedInitialQueryParams) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab },
        queryParamsHandling: 'merge'
      });
    }

    this.tfs.search(this.getCurrentFilters());
    this.ensureMissingStatusesLoadedForCurrentFilters();
  }

  private getRequestedStatusesFromFilters(explicitStatuses?: string[]): string[] {
    const statuses = Array.isArray(explicitStatuses)
      ? explicitStatuses
      : (Array.isArray(this.getCurrentFilters().status) ? this.getCurrentFilters().status : []);
    return statuses.map((status: string) => status.toLowerCase());
  }

  private updateSelectedStatusTabFromStatuses(statuses: string[]): void {
    const normalized = statuses.map(status => this.normalizeStatusFilterValue(status)).sort();

    if (this.statusSetsMatch(normalized, this.activeStatusPreset)) {
      this.selectedStatusTab = 'active';
      return;
    }

    if (this.statusSetsMatch(normalized, this.completedStatusPreset)) {
      this.selectedStatusTab = 'completed';
      return;
    }

    if (this.statusSetsMatch(normalized, this.allStatusPreset)) {
      this.selectedStatusTab = 'all';
      return;
    }

    this.selectedStatusTab = 'custom';
  }

  private statusSetsMatch(selected: string[], preset: string[]): boolean {
    const sortedPreset = [...preset].map(status => this.normalizeStatusFilterValue(status)).sort();
    return selected.length === sortedPreset.length &&
      selected.every((status, index) => status === sortedPreset[index]);
  }

  private getMissingStatusesToFetch(selectedStatuses: string[]): string[] {
    if (this.initialFetchWasDateFiltered) {
      return [];
    }

    const defaultActiveStatuses = ['proposed', 'booked', 'arrived', 'pending', 'waitlist'];
    const requested = selectedStatuses.filter(Boolean);
    if (!requested.length) {
      return [];
    }

    return requested.filter(status => !defaultActiveStatuses.includes(status) && !this.currentLoadedStatusSet.has(status));
  }

  private ensureMissingStatusesLoadedForCurrentFilters(explicitStatuses?: string[]): void {
    if (this.initialFetchWasDateFiltered) {
      return;
    }

    const requestedStatuses = this.getRequestedStatusesFromFilters(explicitStatuses);
    const missingStatuses = this.getMissingStatusesToFetch(requestedStatuses);
    if (!missingStatuses.length) {
      return;
    }

    const userRole = this.authService.user.getValue()?.role;
    const isAdministrative = isHospitalAdmin(userRole) || isHealthcareAdmin(userRole);
    const orgId = this.authService.user.getValue()?.organizationId;
    const actorRef = this.getLoggedInActorReference();

    this.fetchAdditionalStatusesForCurrentFilters(isAdministrative, orgId, actorRef, missingStatuses);
  }

  private fetchAdditionalStatusesForCurrentFilters(
    isAdministrative: boolean,
    orgId?: string,
    actorRef?: string,
    statuses?: string[]
  ): void {
    const missingStatuses = (statuses ?? this.getMissingStatusesToFetch(this.getRequestedStatusesFromFilters()))
      .map((status: string) => status.toLowerCase())
      .filter(Boolean);

    if (!missingStatuses.length || !orgId && !actorRef) {
      return;
    }

    const requests = missingStatuses.map(status => this.fetchAppointmentsByStatus(status, isAdministrative, orgId, actorRef));
    forkJoin(requests).pipe(
      map((results: Appointment[][]) => results.flat()),
      map((rows: Appointment[]) => this.dedupeAppointments([...this.allLoadedAppointments, ...rows])),
      tap((rows: Appointment[]) => {
        const transformedRows = this.applyLoadedAppointments(rows);
        this.tfs.setLocalRows(transformedRows);
      })
    ).subscribe();
  }

  private fetchAppointmentsByStatus(
    status: string,
    isAdministrative: boolean,
    orgId?: string,
    actorRef?: string
  ): Observable<Appointment[]> {
    if (isAdministrative && orgId) {
      const owned$ = this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
        resourceType: 'Appointment',
        urlSearchObject: { _tag: orgId, status },
        scope: 'orgWide',
        tier1FollowNextLinks: true,
        tier1MaxPages: 100
      });
      const shared$ = this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
        resourceType: 'Appointment',
        urlSearchObject: { _security: orgId, status },
        scope: 'orgWide',
        tier1FollowNextLinks: true
      });

      return combineLatest([owned$, shared$]).pipe(
        map(([owned, shared]) => this.dedupeAppointments([...(owned || []), ...(shared || [])]))
      );
    }

    if (actorRef) {
      return this.utilityService.threeTierQueryAndSyncToState$<Appointment>({
        resourceType: 'Appointment',
        urlSearchObject: { actor: actorRef, status },
        context: new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true),
        querySearchObject: [
          { key: 'participant.actor.reference', value: [actorRef, `${this.backendUrl}/${actorRef}`] }
        ],
        scope: 'orgWide',
        tier1FollowNextLinks: true
      }).pipe(map(rows => this.dedupeAppointments(Array.isArray(rows) ? rows : [])));
    }

    return of([]);
  }

  private applyLoadedAppointments(rows: Appointment[]): any[] {
    if (this.isHealthcareAdmin()) {
      rows = rows.filter(r => 
        r.appointmentType?.coding?.[0]?.code === 'in-person' &&
        r.serviceCategory?.[0]?.coding?.[0]?.code === 'consultation'
      );
    }
    this.allLoadedAppointments = this.dedupeAppointments(rows).map(row => this.toAppointmentTableRow(row));
    this.currentLoadedStatusSet = new Set(this.allLoadedAppointments.map(a => this.normalizeStatusFilterValue(a.status)).filter(Boolean));
    return this.allLoadedAppointments;
  }

  onPage(event: PageEvent): void {
    // Delegate to TableFilteringService which will page locally when enabled
    this.tfs.changePage(event.pageIndex);
  }

  private resolveAppointmentDateValue(resource: Appointment, field: 'start' | 'end'): string | null {
    const direct = (resource as any)?.[field];
    if (direct) {
      return direct;
    }

    const requestedPeriod = (resource as any)?.requestedPeriod;
    if (Array.isArray(requestedPeriod) && requestedPeriod.length) {
      const value = requestedPeriod[0]?.[field];
      if (value) {
        return value;
      }
    }

    if (requestedPeriod && typeof requestedPeriod === 'object') {
      const value = (requestedPeriod as any)?.[field];
      if (value) {
        return value;
      }
    }

    const slot = Array.isArray((resource as any)?.slot) ? (resource as any)?.slot : [];
    const slotItem = slot[0];
    if (slotItem && (slotItem as any)?.[field]) {
      return (slotItem as any)?.[field];
    }

    return null;
  }

  private toAppointmentTableRow(resource: Appointment): any {
    return {
      ...resource,
      date: this.resolveAppointmentDateValue(resource, 'start'),
      end: this.resolveAppointmentDateValue(resource, 'end') || resource.end || null,
      displayType: this.resolveAppointmentDisplayType(resource),
      serviceCategoryDisplay: this.resolveAppointmentServiceCategory(resource),
      specialtyDisplay: this.resolveAppointmentSpecialty(resource),
      patientRef: this.getPatientReference(resource)
    };
  }

  private resolveAppointmentSpecialty(a: Appointment): string {
    const specialty = a.specialty?.[0];
    return specialty?.coding?.[0]?.display ||
      specialty?.text ||
      specialty?.coding?.[0]?.code ||
      '';
  }

  private dedupeAppointments(appointments: Appointment[]): Appointment[] {
    const seen = new Set<string>();
    return appointments.filter((appointment: Appointment) => {
      if (!appointment?.id) return true;
      if (seen.has(appointment.id)) return false;
      seen.add(appointment.id);
      return true;
    });
  }

  private customFilterPredicate(row: any, filters: any): boolean {
    // 1. Status Filter
    if (filters.status?.length) {
      const rowStatus = this.normalizeStatusFilterValue(row.status);
      const selectedStatuses = filters.status.map((status: string) => this.normalizeStatusFilterValue(status));
      if (!selectedStatuses.includes(rowStatus)) return false;
    }

    // 2. Service Category Filter (Telemedicine vs Consultation)
    if (filters.serviceCategory?.length) {
      const isTele = this.isTelemedicine(row);
      const isConsult = this.isConsultation(row);
      const showTele = filters.serviceCategory.includes('Telemedicine');
      const showConsult = filters.serviceCategory.includes('Consultation');

      if (showTele && !showConsult && !isTele) return false;
      if (showConsult && !showTele && !isConsult) return false;
      if (showTele && showConsult && !isTele && !isConsult) return false;
      if (!showTele && !showConsult) { /* No filter applied */ }
    }

    // 3. Date Range Filter
    if (filters.dateRange?.length) {
      if (!this.matchesDateRange(row, filters.dateRange)) return false;
    }

    // 4. Search term (case-insensitive on patient name/display)
    if (filters._search) {
      const term = filters._search.toLowerCase();
      const patientName = (row.displayType || '').toLowerCase(); // Or use a flattened string of the whole row
      // For more comprehensive search, we could flatten the patient info
      if (!patientName.includes(term) && !row.id?.toLowerCase().includes(term)) return false;
    }

    return true;
  }

  private normalizeStatusFilterValue(status: string | undefined | null): string {
    const normalized = (status ?? '').toString().trim().toLowerCase();
    if (['no-show', 'no show', 'noshow'].includes(normalized)) return 'noshow';
    return normalized;
  }

  private isTelemedicine(a: Appointment): boolean {
    if (this.hasServiceCategory(a, 'telemedicine')) {
      return true;
    }

    if (this.hasServiceCategory(a, 'consultation')) {
      return false;
    }

    return !!a.appointmentType?.coding?.some(c =>
      this.isVirtualAppointmentType(
        this.normalizeTerminologyValue(c.code),
        this.normalizeTerminologyValue(c.display),
        this.normalizeTerminologyValue(a.appointmentType?.text)
      )
    );
  }

  private isConsultation(a: Appointment): boolean {
    if (this.hasServiceCategory(a, 'consultation')) {
      return true;
    }

    if (this.hasServiceCategory(a, 'telemedicine')) {
      return false;
    }

    return !!a.appointmentType?.coding?.some(c =>
      this.isConsultationServiceCategory(
        this.normalizeTerminologyValue(c.code),
        this.normalizeTerminologyValue(c.display),
        this.normalizeTerminologyValue(a.appointmentType?.text)
      )
    );
  }

  private hasServiceCategory(a: Appointment, category: 'telemedicine' | 'consultation'): boolean {
    const resolvedCategory = this.resolveAppointmentServiceCategory(a).toLowerCase();

    return category === 'telemedicine'
      ? resolvedCategory === 'telemedicine'
      : resolvedCategory === 'consultation';
  }

  private matchesDateRange(row: any, ranges: string[]): boolean {
    if (!row.date) return false;
    const apptDate = new Date(row.date);
    const now = new Date();

    return ranges.some(range => {
      if (range === 'Today') {
        return apptDate.toDateString() === now.toDateString();
      }
      if (range === 'This Week') {
        const startOfWeek = new Date(now);
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 7);
        return apptDate >= startOfWeek && apptDate < endOfWeek;
      }
      if (range === 'This Month' || range === 'This month') {
        return apptDate.getFullYear() === now.getFullYear() && apptDate.getMonth() === now.getMonth();
      }
      if (range === 'Last 30 Days') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(now.getDate() - 30);
        return apptDate >= thirtyDaysAgo;
      }
      if (range === 'This Year') {
        return apptDate.getFullYear() === now.getFullYear();
      }
      if (range === 'All Time') return true;
      return false;
    });
  }

  // override ngOnDestroy() {
  //   this.subs.unsubscribe();
  // }

  getLoggedInActorReference(): string {
    const user = this.authService.user.getValue();
    if (!user) return '';
    if (user.role && ['doctor', 'healthcareassistant', 'lab', 'pharmacy', 'remotedoctor', 'hospitaladmin', 'healthcareadmin', 'alliedhealthprofessional', 'cashier'].includes(user.role)) {
      return `Practitioner/${user['fhirResourceId'] || user['fhirResourceId']}`;
    }
    if (user.role === 'patient') {
      return `Patient/${user['fhirResourceId'] || user['fhirResourceId']}`;
    }
    return '';
  }

  getPatientReference(appointment: any): string | null {
    if (!appointment.participant) return null;
    for (const p of appointment.participant) {
      const ref = p.actor?.reference;
      if (ref && ref.includes('Patient/')) return ref;
    }
    return null;
  }

  getPractitionerReferences(appointment: any): string[] {
    const participants = Array.isArray(appointment?.participant) ? appointment.participant : [];
    const refs = participants
      .map((participant: any) => participant?.actor?.reference)
      .filter((ref: any): ref is string => typeof ref === 'string' && ref.includes('Practitioner/'))
      .map((ref: string) => this.normalizeReference(ref, 'Practitioner'))
      .filter(Boolean);

    return Array.from(new Set(refs));
  }

  private normalizeReference(reference: string, resourceType: string): string {
    const raw = (reference ?? '').toString().split('?')[0].trim();
    const marker = `${resourceType}/`;
    const markerIndex = raw.indexOf(marker);
    if (markerIndex >= 0) {
      return raw.slice(markerIndex);
    }

    const segments = raw.split('/').filter(Boolean);
    const id = segments.length ? segments[segments.length - 1] : '';
    return id ? `${resourceType}/${id}` : '';
  }

  showAppointmentDetails(appointment: any): void {
    console.log('📋 Opening appointment details for:', appointment.id);
    this.dialog.open(AppointmentDetailsComponent, {
      maxHeight: '93vh',
      maxWidth: '90vh',
      data: {
        resourceData: appointment,
        detailsBuilderObject: this.appointmentDetailsBuilder
      }
    });
  }

  hasMeetingLink(appointment: any): boolean {
    return !!this.getMeetingLink(appointment);
  }

  openMeetingLink(appointment: any): void {
    const appointmentId = (appointment?.id ?? '').toString().trim();
    if (!appointmentId) {
      this.sn.open('No appointment was specified for this meeting.', 'Close', { duration: 3000 });
      return;
    }

    void this.navigateToZoomMeeting(appointment, appointmentId);
  }

  private async navigateToZoomMeeting(appointment: any, appointmentId: string): Promise<void> {
    const patientRef = this.getPatientReference(appointment);
    const patientId = this.extractPatientId(patientRef);
    const currentRole = String(this.authService.user.getValue()?.role ?? '').toLowerCase();
    const meetingRole = currentRole === 'patient' ? 0 : 1;

    if (patientId && await this.patientExistsInCurrentTenant(patientId)) {
      this.router.navigate(
        ['/app/patients', patientId, 'zoom-meeting'],
        { queryParams: { role: meetingRole, appointmentId } }
      );
      return;
    }

    this.router.navigate(
      ['/app/zoom-meetings', appointmentId],
      { queryParams: { role: meetingRole, patientId: patientId || undefined, patientContextMissing: patientId ? true : undefined } }
    );
  }

  private getMeetingLink(appointment: any): string {
    if (!appointment) return '';

    // 1) Some API responses may place it directly.
    const direct = this.normalizeMeetingUrl(
      appointment.zoomMeetingUrl || appointment.meetingLink || appointment.meetingUrl
    );
    if (direct) return direct;

    // 2) FHIR-style: try extension values.
    const ext = Array.isArray(appointment.extension) ? appointment.extension : [];
    for (const ex of ext) {
      const url = this.normalizeMeetingUrl(ex?.valueUrl || ex?.valueUri || ex?.valueString);
      if (url) return url;

      const extUrl = (ex?.url ?? '').toString().toLowerCase();
      if (extUrl.includes('zoom') || extUrl.includes('meeting') || extUrl.includes('tele')) {
        const candidate = this.normalizeMeetingUrl(ex?.valueUrl || ex?.valueUri || ex?.valueString);
        if (candidate) return candidate;
      }
    }

    // 3) Last resort: extract the first URL from human text fields.
    const textCandidates: string[] = [
      appointment.comment,
      appointment.patientInstruction,
      appointment.description
    ].filter((v: any) => typeof v === 'string' && !!v.trim());

    for (const text of textCandidates) {
      const match = text.match(this.meetingUrlRegex);
      if (match?.[1]) {
        const url = this.normalizeMeetingUrl(match[1]);
        if (url) return url;
      }
    }

    return '';
  }

  private extractPatientId(patientRef: string | null): string {
    if (!patientRef) {
      return '';
    }

    const normalized = patientRef.split('?')[0].trim();
    const segments = normalized.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : '';
  }

  private async patientExistsInCurrentTenant(patientId: string): Promise<boolean> {
    try {
      const bundle = await firstValueFrom(
        this.fhirHttp.get<Bundle>(`${this.fhirBackendUrl}/Patient?_id=${encodeURIComponent(patientId)}&_count=1`, {
          context: new HttpContext().set(LoadingUIEnabled, false)
        }).pipe(catchError(() => of(null)))
      );
      return !!bundle?.entry?.some((entry: any) => entry.resource?.id === patientId);
    } catch {
      return false;
    }
  }

  private normalizeMeetingUrl(value: any): string {
    const raw = (value ?? '').toString().trim();
    if (!raw) return '';

    // Prevent javascript: or other dangerous schemes.
    const withProtocol = raw.startsWith('http://') || raw.startsWith('https://') ? raw : `https://${raw}`;
    try {
      const u = new URL(withProtocol);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
      return u.toString();
    } catch {
      return '';
    }
  }

  private openUrlInNewTab(url: string): void {
    const safe = this.normalizeMeetingUrl(url);
    if (!safe) {
      this.sn.open('Invalid meeting link.', 'Close', { duration: 3000 });
      return;
    }

    const win = window.open(safe, '_blank', 'noopener,noreferrer');
    if (!win) {
      this.sn.open('Popup blocked. Please allow popups for this site.', 'Close', { duration: 4000 });
    }
  }

  respondToAppointment(appointment: any): void {
    console.log('🔄 Opening response dialog for appointment:', appointment.id);

    const dialogRef = this.dialog.open(AppointmentResponseDialogComponent, {
      width: '500px',
      disableClose: false,
      data: {
        appointmentId: appointment.id,
        appointmentDescription: appointment.description
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.confirmed) {
        this.handleAppointmentResponse(appointment, result);
      }
    });
  }

  private handleAppointmentResponse(appointment: any, result: any): void {
    if (result.action === 'accept') {
      this.acceptAppointment(appointment, result.confirmOnBehalfOfPatientId);
    } else if (result.action === 'decline') {
      this.declineAppointment(appointment, result.declineReason);
    }
  }

  private acceptAppointment(appointment: any, confirmOnBehalfOfPatientId?: string): void {
    console.log('✅ Accepting appointment:', appointment.id);

    this.appointmentService.respondToAppointment(
      appointment.id,
      { status: 'accepted' },
      undefined,
      confirmOnBehalfOfPatientId
    ).subscribe({
      next: (response) => {
        console.log('✅ Appointment accepted:', response);
        appointment.status = response.appointmentStatus || 'booked';
        this.tableDataSource.data = [...this.tableDataSource.data];
        this.sn.openFromComponent(SuccessMessageComponent, {
          data: { message: response.message || 'Appointment accepted successfully!' },
          duration: 3000
        });
      },
      error: (err) => {
        console.error('❌ Error accepting appointment:', err);
        this.handleAppointmentError(err);
      }
    });
  }

  private declineAppointment(appointment: any, declineReason?: string): void {
    console.log('❌ Declining appointment:', appointment.id);

    this.appointmentService.respondToAppointment(
      appointment.id,
      { status: 'declined' },
      declineReason
    ).subscribe({
      next: (response) => {
        console.log('✅ Appointment declined:', response);
        appointment.status = 'cancelled';
        this.tableDataSource.data = [...this.tableDataSource.data];
        this.sn.open('Appointment declined successfully', 'Close', { duration: 3000 });
      },
      error: (err) => {
        console.error('❌ Error declining appointment:', err);
        this.handleAppointmentError(err);
      }
    });
  }

  private handleAppointmentError(err: any): void {
    if (err.status === 409) {
      this.sn.open('Time slot conflict detected.', 'Close', { duration: 3000 });
    } else if (err.status === 403) {
      this.sn.open('You do not have permission to respond to this appointment.', 'Close', { duration: 3000 });
    } else if (err.status === 400) {
      this.sn.open(err.error?.message || 'Invalid request. Cannot respond to this appointment.', 'Close', { duration: 3000 });
    } else {
      this.sn.open('Failed to respond to appointment. Please try again.', 'Close', { duration: 3000 });
    }
  }

  isAdmin(): boolean {
    const role = this.authService.user.getValue()?.role;
    return isHospitalAdmin(role) || isHealthcareAdmin(role);
  }

  isHCA(): boolean {
    const role = this.authService.user.getValue()?.role;
    return isHealthcareAdmin(role);
  }

  isInPersonConsultation(a: Appointment): boolean {
    const isConsult = a.serviceCategory?.some(sc => sc.coding?.some(c => c.code === 'consultation')) || false;
    const isInPerson = a.appointmentType?.coding?.some(c => c.code === 'in-person') || false;
    return isConsult && isInPerson;
  }

  openShiftDialog(appointment: Appointment) {
    const dialogRef = this.dialog.open(ShiftCascadeDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      data: { appointment }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchData();
        this.sn.open('Appointments successfully shifted.', 'Close', { duration: 3000 });
      }
    });
  }

  isPatient(): boolean {
    const role = this.authService.user.getValue()?.role;
    return role === 'patient';
  }

  isHealthcareAdmin(): boolean {
    const role = this.authService.user.getValue()?.role;
    return role === 'healthcareadmin';
  }

  confirmCancellation(appointment: Appointment) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Cancel Appointment',
        message: 'Are you sure you want to cancel this appointment? This action will notify all participants.',
        confirmText: 'Yes, cancel it',
        cancelText: 'No'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cancelAppointment(appointment);
      }
    });
  }

  private cancelAppointment(appointment: Appointment) {
    const updated = JSON.parse(JSON.stringify(appointment)) as any;
    updated.status = 'cancelled';

    // Strip custom UI properties added by toAppointmentTableRow
    delete updated.date;
    delete updated.displayType;
    delete updated.serviceCategoryDisplay;
    delete updated.specialtyDisplay;
    delete updated.patientRef;

    this.fhirHttp.put<Appointment>(`${this.fhirBackendUrl}/Appointment/${appointment.id}`, updated, {
      headers: {
        'Content-Type': 'application/fhir+json',
        Accept: 'application/fhir+json',
        Prefer: 'return=representation'
      }
    }).subscribe({
      next: (res) => {
        this.sn.open('Appointment cancelled successfully.', 'Close', { duration: 3000 });
        this.notifyParticipants(updated);
        this.fetchData();
      },
      error: (err) => {
        console.error('Cancel failed', err);
        this.sn.open('Error cancelling appointment.', 'Close', { duration: 3000 });
      }
    });
  }

  private notifyParticipants(appointment: Appointment) {
    this.unifiedNotificationService.notifyAppointmentCancellations([appointment]);
  }

  reassignPractitioner(appointment: any): void {
    const dialogRef = this.dialog.open(BookingsFormComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      data: { appointment }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.fetchData();
      }
    });
  }



  appointmentDetailsBuilder: DetailsBuilderObject = {
    resourceName: 'Appointment',
    resourceIcon: 'calendar_today',
    specialHeader: {
      strongSectionKey: 'description',
      iconSectionKeys: ['status', 'priority'],
      contentSectionKeys: ['start', 'end']
    },
    groups: [
      { groupName: 'General', groupIcon: 'info', groupKeys: ['status', 'appointmentType', 'reasonCode'] },
      { groupName: 'Schedule', groupIcon: 'schedule', groupKeys: ['start', 'end', 'minutesDuration'] },
      { groupName: 'Participants', groupIcon: 'people', groupKeys: ['participant'] },
      { groupName: 'Notes', groupIcon: 'notes', groupKeys: ['comment', 'patientInstruction'] }
    ]
  };
}

// ═══════════════════════════════════════════════════════════════
// DIALOG COMPONENT FOR APPOINTMENT RESPONSE
// ═══════════════════════════════════════════════════════════════

@Component({
  selector: 'app-appointment-response-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatIconModule,
    MatDividerModule
  ],
  template: `
    <div class="appointment-response-dialog">
      <h2 mat-dialog-title>
        <mat-icon style="margin-right: 8px; color: #1976d2;">help_outline</mat-icon>
        Respond to Appointment
      </h2>

      <mat-dialog-content>
        <p class="appointment-description">
          <strong>Appointment:</strong> {{ data.appointmentDescription || 'Consultation' }}
        </p>

        <mat-divider style="margin: 16px 0;"></mat-divider>

        <!-- ACCEPT FORM -->
        <form [formGroup]="acceptForm" *ngIf="!showDeclineForm">
          <div class="form-section">
            <h3 style="color: #4caf50; margin-bottom: 12px;">
              <mat-icon style="margin-right: 8px; vertical-align: middle;">check_circle</mat-icon>
              Accept Appointment
            </h3>

            <p style="margin-bottom: 16px; color: #666;">
              Confirm your attendance for this appointment.
            </p>
   <!--
            <mat-form-field appearance="fill" class="full-width" *ngIf="isCurrentUserPractitioner()">
              <mat-label>Confirm on behalf of patient (optional)</mat-label>
              <input
                matInput
                formControlName="confirmOnBehalfOfPatientId"
                placeholder="Leave blank if patient will confirm themselves">
              <mat-hint>Only needed if patient has no account</mat-hint>
            </mat-form-field>-->
          </div>
        </form>

        <!-- DECLINE FORM -->
        <form [formGroup]="declineForm" *ngIf="showDeclineForm">
          <div class="form-section">
            <h3 style="color: #f44336; margin-bottom: 12px;">
              <mat-icon style="margin-right: 8px; vertical-align: middle;">cancel</mat-icon>
              Decline Appointment
            </h3>

            <p style="margin-bottom: 16px; color: #666;">
              Please provide a reason for declining this appointment.
            </p>

            <mat-form-field appearance="fill" class="full-width">
              <mat-label>Reason for declining</mat-label>
              <textarea
                matInput
                formControlName="declineReason"
                placeholder="e.g., Schedule conflict, Medical emergency, Other"
                rows="4"></textarea>
              <mat-hint>This will be communicated to other participants</mat-hint>
            </mat-form-field>
          </div>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()">Cancel</button>

        <!-- TOGGLE BUTTON -->
<!-- TOGGLE BUTTON -->
<button
  mat-stroked-button
  (click)="toggleForm()"
  [color]="showDeclineForm ? 'primary' : 'warn'"
  style="margin-right: 8px;">
  {{ showDeclineForm ? '✓ Accept Instead' : '✕ Decline Instead' }}
</button>

<!-- SUBMIT BUTTON -->
<button
  mat-raised-button
  (click)="onConfirm()"
  [color]="showDeclineForm ? 'warn' : 'primary'"
  [disabled]="(showDeclineForm ? declineForm : acceptForm).invalid">
  {{ showDeclineForm ? 'Decline' : 'Accept' }}
</button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .appointment-response-dialog {
      min-width: 400px;
      padding: 20px;
    }

    .appointment-description {
      background-color: #f5f5f5;
      padding: 12px;
      border-left: 4px solid #1976d2;
      border-radius: 4px;
      margin-bottom: 16px;
    }

    .form-section {
      margin: 16px 0;
    }

    .full-width {
      width: 100%;
    }

    .accept-active {
      color: #4caf50;
      border-color: #4caf50;
    }

    .decline-active {
      color: #f44336;
      border-color: #f44336;
    }

    .green-button {
      background-color: #4caf50;
      color: white;
    }

    .green-button:hover {
      background-color: #45a049;
    }

    .red-button {
      background-color: #f44336;
      color: white;
    }

    .red-button:hover {
      background-color: #da190b;
    }

    mat-dialog-actions {
      margin-top: 24px;
      padding-top: 16px;
      border-top: 1px solid #e0e0e0;
    }

    h3 {
      font-size: 16px;
      font-weight: 600;
    }

    p {
      font-size: 14px;
    }

    mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }
  `]
})
export class AppointmentResponseDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);

  // @inject()
  dialogRef = inject(MatDialogRef<AppointmentResponseDialogComponent>);


  data = inject(MAT_DIALOG_DATA);

  acceptForm!: FormGroup;
  declineForm!: FormGroup;
  showDeclineForm = false;

  ngOnInit(): void {

    this.initializeForms();
  }

  private initializeForms(): void {
    this.acceptForm = this.fb.group({
      confirmOnBehalfOfPatientId: ['']
    });

    this.declineForm = this.fb.group({
      declineReason: ['', Validators.required]
    });
  }

  isCurrentUserPractitioner(): boolean {
    const user = this.authService.user.getValue();
    return !!user?.role && ['doctor', 'healthcareassistant', 'remotedoctor'].includes(user.role);
  }


  toggleForm(): void {
    this.showDeclineForm = !this.showDeclineForm;
  }

  onConfirm(): void {
    if (this.showDeclineForm) {
      if (this.declineForm.valid) {
        this.dialogRef.close({
          confirmed: true,
          action: 'decline',
          declineReason: this.declineForm.get('declineReason')?.value
        });
      }
    } else {
      if (this.acceptForm.valid) {
        this.dialogRef.close({
          confirmed: true,
          action: 'accept',
          confirmOnBehalfOfPatientId: this.acceptForm.get('confirmOnBehalfOfPatientId')?.value || undefined
        });
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false });
  }
}
